/**
 * Översätter HTTP-fel till meningar en användare kan agera på.
 *
 * Bakgrund: uppladdningsfel har vid flera tillfällen visats som rå JSON mitt i
 * gränssnittet — `{"detail":"No access to module 'p.dokument'"}` följt av
 * `HTTP 403`. Det säger ingenting till den som ska bifoga en offert eller välja
 * en ritning, och ser ut som att något är sönder.
 *
 * Hjälparen låg först bara i Inköps API-lager (skapad 2026-08-14). När dagbokens
 * ritningsväljare 2026-09-11 visade exakt samma råa JSON flyttades den hit, så
 * att nästa modul med samma behov inte gör en tredje kopia.
 */

/**
 * @param {number} status   HTTP-statuskod
 * @param {unknown} detail  `detail` ur svarskroppen, om den gick att läsa
 * @param {string} fallback Text att visa när inget annat passar
 * @param {{ behorighet?: string, saknas?: string }} [texter]
 *   `behorighet` — egen 403-text, t.ex. "Du saknar behörighet att lägga till offerter."
 *   `saknas`     — egen 404-text, t.ex. "Inköpet hittades inte."
 * @returns {string} en mening utan teknisk jargong
 */
export function felmeddelande(status, detail, fallback, texter = {}) {
  if (status === 401) {
    return 'Din inloggning har gått ut. Ladda om sidan och logga in igen.';
  }
  if (status === 403) {
    // Backend svarar sedan 2026-09-11 med svensk text som namnger modulen
    // ("Du saknar behörighet till modulen Projekt: Dokument"). Den är mer
    // precis än vår generella formulering, så den vinner när den finns.
    if (_arLasbar(detail)) return detail;
    return texter.behorighet || 'Du saknar behörighet för den här åtgärden. Kontakta din administratör.';
  }
  if (status === 404) {
    if (texter.saknas) return texter.saknas;
    if (_arLasbar(detail)) return detail;
    return 'Det du försökte öppna hittades inte. Det kan ha tagits bort av någon annan.';
  }
  if (status === 409) {
    if (_arLasbar(detail)) return detail;
    return 'Filen kunde inte lagras. Kontrollera att den inte redan finns i projektet.';
  }
  if (status === 413) {
    return 'Filen är för stor för att laddas upp.';
  }
  if (status >= 500) {
    return 'Servern svarade inte som väntat. Försök igen om en stund.';
  }
  if (_arLasbar(detail)) return detail;
  return fallback;
}

/**
 * Backend-detaljer är ofta redan på svenska och mer precisa än våra generella
 * texter. Rå JSON och engelsk teknisk text släpps däremot inte igenom — det är
 * just det som gjorde felrutorna obegripliga.
 */
function _arLasbar(detail) {
  if (typeof detail !== 'string') return false;
  const text = detail.trim();
  if (!text) return false;
  if (text.startsWith('{') || text.startsWith('[')) return false;
  // En HTML-sida (proxyfel, 502 fran lastbalanserararen) sager anvandaren
  // ingenting - den ska mota reservtexten i stallet.
  if (text.startsWith('<')) return false;
  // Engelska standardsvar från ramverket och äldre endpoints.
  if (/^(No access|Not authenticated|Not Found|Internal Server Error|Forbidden|Unauthorized)/i.test(text)) {
    return false;
  }
  return true;
}

/**
 * Felmeddelande för en vy som öppnas via en extern delningslänk.
 *
 * Den som klickar är en utomstående — beställare, partner, yrkesarbetare — utan
 * konto, utan kännedom om KanPlan och utan någon att fråga. En naken statuskod
 * ger dem ingenting att göra. Nästan alla fel i de här vyerna har dessutom
 * samma orsak: token har gått ut, dragits tillbaka eller skrivits fel.
 *
 * Backend-detaljer används INTE här. De är skrivna för inloggade användare och
 * kan röja intern struktur för någon utanför organisationen.
 *
 * @param {number} status HTTP-statuskod
 * @returns {string} en mening som säger vad mottagaren kan göra
 */
export function felmeddelandeDelningslank(status) {
  if (status === 404 || status === 410) {
    return 'Länken är inte längre giltig. Be din kontaktperson skicka en ny.';
  }
  if (status === 401 || status === 403) {
    return 'Länken ger inte längre åtkomst. Be din kontaktperson skicka en ny.';
  }
  if (status >= 500) {
    return 'Sidan kunde inte laddas just nu. Försök igen om en stund.';
  }
  return 'Sidan kunde inte laddas. Kontrollera att du öppnat hela länken.';
}


/**
 * Läser `detail` ur ett felsvar utan att kasta om kroppen inte är JSON.
 * @param {Response} res
 * @returns {Promise<string|null>}
 */
export async function lasFeldetalj(res) {
  let text;
  try {
    text = await res.text();
  } catch {
    return null;
  }
  if (!text) return null;
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    // Inte JSON. Kan vara en ren textrad fran en aldre endpoint - den duger,
    // men en HTML-felsida gor det inte. _arLasbar sallar bort den senare.
    return text.trim().slice(0, 300) || null;
  }
  const detail = data && data.detail !== undefined ? data.detail : data;
  if (typeof detail === 'string') return detail.trim() || null;
  if (Array.isArray(detail)) {
    // Pydantic-valideringsfel ar en lista med {msg} - plocka meningarna sa
    // anvandaren far veta VILKET falt som klickade fel, inte bara att det gick fel.
    const rader = detail.map((x) => (x && (x.msg || x.message)) || '').filter(Boolean);
    return rader.length ? rader.join('. ') : null;
  }
  if (detail && typeof detail === 'object') {
    const d = detail.message || detail.msg;
    return typeof d === 'string' && d.trim() ? d : null;
  }
  return null;
}
